import React, { useEffect, useMemo, useState } from "react";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import "./PartnerAdminDashboard.css";

// ปรับ URL นี้ให้ตรงกับ backend จริงของคุณ (เหมือนกับใน PartnerApplyForm.jsx)
const API_URL = process.env.REACT_APP_API || "http://localhost:4000/api";

const STATUS_LABEL = {
  pending: "รอตรวจสอบ",
  reviewing: "กำลังตรวจสอบ",
  approved: "อนุมัติแล้ว",
  rejected: "ปฏิเสธ",
};

const STATUS_OPTIONS = ["all", "pending", "reviewing", "approved", "rejected"];

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString("th-TH", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const PartnerAdminDashboard = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchPartners = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/partner`);
      if (!res.ok) throw new Error("โหลดข้อมูลไม่สำเร็จ");
      const data = await res.json();
      setPartners(data);
    } catch (err) {
      console.error(err);
      setError(err.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const filteredPartners = useMemo(() => {
    return partners.filter((p) => {
      const matchStatus =
        statusFilter === "all" || p.status === statusFilter;

      const q = search.trim().toLowerCase();
      const matchSearch =
        !q ||
        p.name?.toLowerCase().includes(q) ||
        p.email?.toLowerCase().includes(q) ||
        p.phone?.toLowerCase().includes(q) ||
        p.organization?.toLowerCase().includes(q);

      return matchStatus && matchSearch;
    });
  }, [partners, search, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts = { all: partners.length };
    for (const p of partners) {
      counts[p.status] = (counts[p.status] || 0) + 1;
    }
    return counts;
  }, [partners]);

  return (
    <div className="partner-admin-page">
      <div className="partner-admin-header">
        <div>
          <h2 className="partner-admin-title">รายการสมัคร Partner</h2>
          <p className="partner-admin-subtitle">
            ทั้งหมด {partners.length} รายการ
          </p>
        </div>
        <button
          className="partner-admin-refresh-btn"
          onClick={fetchPartners}
          disabled={loading}
        >
          <ReloadOutlined spin={loading} /> รีเฟรช
        </button>
      </div>

      {/* Filter แถบสถานะ */}
      <div className="partner-admin-filters">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s}
            className={`partner-admin-filter-chip ${
              statusFilter === s ? "active" : ""
            }`}
            onClick={() => setStatusFilter(s)}
          >
            {s === "all" ? "ทั้งหมด" : STATUS_LABEL[s]}
            <span className="chip-count">{statusCounts[s] || 0}</span>
          </button>
        ))}
      </div>

      {/* ช่องค้นหา */}
      <div className="partner-admin-search">
        <SearchOutlined />
        <input
          type="text"
          placeholder="ค้นหาชื่อ, อีเมล, เบอร์โทร, องค์กร..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* เนื้อหา */}
      {loading && <div className="partner-admin-state">กำลังโหลดข้อมูล...</div>}

      {!loading && error && (
        <div className="partner-admin-state error">
          ❌ {error}
          <button onClick={fetchPartners}>ลองใหม่</button>
        </div>
      )}

      {!loading && !error && filteredPartners.length === 0 && (
        <div className="partner-admin-state">ไม่พบข้อมูล</div>
      )}

      {!loading && !error && filteredPartners.length > 0 && (
        <div className="partner-admin-table-wrap">
          <table className="partner-admin-table">
            <thead>
              <tr>
                <th>ชื่อผู้ติดต่อ</th>
                <th>เบอร์โทร</th>
                <th>อีเมล</th>
                <th>องค์กร</th>
                <th>สถานะ</th>
                <th>วันที่สมัคร</th>
              </tr>
            </thead>
            <tbody>
              {filteredPartners.map((p) => (
                <tr key={p._id}>
                  <td>{p.name}</td>
                  <td>{p.phone}</td>
                  <td>{p.email}</td>
                  <td>{p.organization}</td>
                  <td>
                    <span className={`status-badge status-${p.status}`}>
                      {STATUS_LABEL[p.status] || p.status}
                    </span>
                  </td>
                  <td>{formatDate(p.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PartnerAdminDashboard;